// Copyright © 2026 ApexRuntime Labs by Shashi (https://github.com/spshah). All rights reserved.

import { useState, useEffect } from 'react';
import { transactionAPI } from '../../api/budget';
import paymentAPI from '../../api/services/budget/paymentService';
import { TRANSACTION_TYPES } from '../../utils/constants';
import { classNames } from '../../utils/helpers';
import { capitalize } from '../../utils/helpers';
import { TYPE_STYLES } from '../../utils/constants';
import CategorySelect from '../categories/CategorySelect';
import { enqueue, isNetworkError } from '../../offline/outbox';
import { cachePaymentModes, getCachedPaymentModes } from '../../offline/db';

const DEFAULT_FORM = {
  type: 'EXPENSE',
  category: '',
  subcategory: '',
  description: '',
  amount: '',
  date: new Date().toISOString().slice(0, 10),
  payment_mode: '',
  // Destination account for a TRANSFER (#31) — payment_mode above is the
  // source leg. Only used/shown when type === 'TRANSFER'.
  destination_payment_mode: '',
  is_recurrent: false,
  recurrent_period: 0,
  recurrent_unit: 'DAYS',
};

const RECURRENCE_DAYS_PER_UNIT = { DAYS: 1, WEEKS: 7, MONTHS: 30 };

// `initial` (an existing transaction, from the API or an optimistic local
// placeholder — both now in the same backend shape, see handleSubmit) uses
// transaction_type/recurrent_period_days; the form's own fields are named
// differently (#26 — this mismatch is exactly why editing a transaction
// used to silently drop its real type and recurrence).
const toFormState = (initial = {}) => {
  const days = initial.recurrent_period_days;
  let recurrent_unit = DEFAULT_FORM.recurrent_unit;
  let recurrent_period = DEFAULT_FORM.recurrent_period;
  if (days) {
    if (days % 30 === 0) {
      recurrent_unit = 'MONTHS';
      recurrent_period = days / 30;
    } else if (days % 7 === 0) {
      recurrent_unit = 'WEEKS';
      recurrent_period = days / 7;
    } else {
      recurrent_unit = 'DAYS';
      recurrent_period = days;
    }
  }
  return {
    ...DEFAULT_FORM,
    ...initial,
    type: initial.transaction_type || DEFAULT_FORM.type,
    recurrent_period,
    recurrent_unit,
  };
};

export default function TransactionForm({ onSuccess, onClose, initial = {} }) {
  const [form, setForm] = useState(() => toFormState(initial));
  const [payModes, setPayModes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  // Set once, from the transaction being edited — a transfer's identity
  // (which pair of legs this is) doesn't change while the form is open.
  const [transferGroup] = useState(initial?.transfer_group ?? null);

  useEffect(() => {
    paymentAPI
      .listModes()
      .then(async (res) => {
        const modes = Array.isArray(res.data) ? res.data : (res.data?.results ?? []);
        setPayModes(modes);
        await cachePaymentModes(modes);
      })
      .catch(async (err) => {
        // Offline (#22): fall back to cache rather than an empty dropdown.
        // A real error (not just offline) still empties it as before.
        setPayModes(isNetworkError(err) ? await getCachedPaymentModes() : []);
      });
  }, []);

  useEffect(() => {
    // `initial` is only one leg of the pair (whichever row the user clicked
    // Edit on in the list) — it doesn't carry the other leg's payment_mode,
    // so the destination/source account fields can't both be known until
    // the sibling leg is fetched (#31).
    if (!transferGroup) return;
    transactionAPI.getTransfer(transferGroup).then((res) => {
      const legs = res.data;
      const outLeg = legs.find((l) => l.transfer_leg === 'OUT');
      const inLeg = legs.find((l) => l.transfer_leg === 'IN');
      if (!outLeg || !inLeg) return;
      setForm((f) => ({
        ...f,
        payment_mode: outLeg.payment_mode ?? '',
        destination_payment_mode: inLeg.payment_mode ?? '',
      }));
    });
  }, [transferGroup]);

  const handleTypeChange = (type) => {
    if (transferGroup) return; // can't convert an existing transfer's type
    setForm((f) => ({ ...f, type, category: '', subcategory: '' }));
  };

  const set = (field) => (e) =>
    setForm((f) => ({ ...f, [field]: e.target.value }));

  const setVal = (field, value) => setForm((f) => ({ ...f, [field]: value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    if (form.type === 'TRANSFER') {
      // A transfer is a linked OUT+IN pair managed as a unit through its
      // own endpoints (#31), not through create()/update() below — those
      // only ever touch one Transaction row, which can't represent both
      // legs. No offline queueing here: that's scoped to plain
      // transactions only (see the catch block below).
      const transferPayload = {
        date: form.date,
        amount: parseFloat(form.amount),
        description: form.description,
        category: form.category || null,
        subcategory: form.subcategory || null,
        source_payment_mode: form.payment_mode,
        destination_payment_mode: form.destination_payment_mode,
      };
      try {
        const res = await (transferGroup
          ? transactionAPI.updateTransfer(transferGroup, transferPayload)
          : transactionAPI.createTransfer(transferPayload));
        onSuccess?.(res.data);
      } catch (err) {
        setError(
          err.response?.data?.detail ||
            Object.values(err.response?.data ?? {})?.[0]?.[0] ||
            'Failed to save transfer.',
        );
      } finally {
        setLoading(false);
      }
      return;
    }

    // Translate the form's own field names to what the backend serializer
    // actually accepts (#26) — sending `type`/`recurrent_period`+
    // `recurrent_unit` as-is are silently ignored as unknown fields, so
    // transaction_type always fell back to its model default (EXPENSE) and
    // recurrence was dropped, however the user filled the form in.
    const { type, destination_payment_mode, recurrent_period, recurrent_unit, ...rest } = form;
    const payload = {
      ...rest,
      amount: parseFloat(form.amount),
      transaction_type: type,
      recurrent_period_days: form.is_recurrent
        ? Number(recurrent_period) * (RECURRENCE_DAYS_PER_UNIT[recurrent_unit] ?? 1)
        : 0,
    };
    try {
      const res = await (initial?.id
        ? transactionAPI.update(initial.id, payload)
        : transactionAPI.create(payload));
      onSuccess?.(res.data);
    } catch (err) {
      if (isNetworkError(err)) {
        // Offline (#22): queue it and let the user carry on — the server's
        // response (real id, computed totals, etc.) arrives once reconnect
        // replays the queue, not now. Editing a transaction that's itself
        // still an unsynced local placeholder (its "id" is `local-<ref>`,
        // not a real server id) must depend on that create's clientRef
        // instead of targeting a bogus id.
        const localPlaceholderRef =
          typeof initial?.id === 'string' && initial.id.startsWith('local-')
            ? initial.id.slice('local-'.length)
            : null;
        const { clientRef } = await enqueue({
          entity: 'transaction',
          op: initial?.id ? 'update' : 'create',
          targetId: localPlaceholderRef ? null : (initial?.id ?? null),
          dependsOnClientRef: localPlaceholderRef,
          payload,
        });
        onSuccess?.({ ...payload, id: initial?.id ?? `local-${clientRef}`, _pending: true });
        return;
      }
      setError(
        err.response?.data?.detail ||
          Object.values(err.response?.data ?? {})?.[0]?.[0] ||
          'Failed to save transaction.',
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      {/* ── Error ──────────────────────────────────────────────── */}
      {error && (
        <p className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2">
          {error}
        </p>
      )}

      {/* ── Transaction type ───────────────────────────────────── */}
      <div>
        <label className="block text-xs font-medium text-gray-600 mb-1">
          Type
        </label>
        <div className="flex gap-2 flex-wrap">
          {TRANSACTION_TYPES.map((t) => (
            <button
              key={t}
              type="button"
              disabled={Boolean(transferGroup)}
              onClick={() => handleTypeChange(t)}
              className={classNames(
                'px-3 py-1.5 rounded-lg text-xs font-medium border transition-colors',
                form.type === t
                  ? (TYPE_STYLES[t]?.active ?? 'bg-gray-600 text-white')
                  : (TYPE_STYLES[t]?.inactive ??
                      'bg-white text-gray-600 border-gray-200'),
                transferGroup && 'opacity-60 cursor-not-allowed',
              )}
            >
              {capitalize(t)}
            </button>
          ))}
        </div>
        {transferGroup && (
          <p className="text-xs text-gray-400 mt-1">
            A transfer's type can't be changed here — delete and recreate it instead.
          </p>
        )}
      </div>

      {/* ── Date + Amount ──────────────────────────────────────── */}
      {/* Stacked below sm: a fixed 2-col grid squeezes iOS Safari's native
          date input into too little width at phone sizes, pushing it into
          the Amount field next to it (#21). */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">
            Date
          </label>
          <input
            type="date"
            value={form.date}
            onChange={set('date')}
            required
            className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
          />
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">
            Amount (₹)
          </label>
          <input
            type="number"
            min="0"
            step="0.01"
            placeholder="0.00"
            value={form.amount}
            onChange={set('amount')}
            required
            className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
          />
        </div>
      </div>

      {/* ── Description ────────────────────────────────────────── */}
      <div>
        <label className="block text-xs font-medium text-gray-600 mb-1">
          Description
        </label>
        <input
          type="text"
          placeholder="What was this for?"
          value={form.description}
          onChange={set('description')}
          required
          className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
        />
      </div>

      {/* ── Category + Payment mode ────────────────────────────── */}
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">
            Category
          </label>
          <CategorySelect
            value={form.category}
            onChange={(catId) => setVal('category', catId)}
            categoryType={form.type}
            disabled={loading}
          />
        </div>
        <div>
          <label
            htmlFor="transaction-payment-mode"
            className="block text-xs font-medium text-gray-600 mb-1"
          >
            {form.type === 'TRANSFER' ? 'From Account' : 'Payment Mode'}
          </label>
          <select
            id="transaction-payment-mode"
            value={form.payment_mode}
            onChange={set('payment_mode')}
            className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
          >
            <option value="">Select…</option>
            {payModes
              // Can't transfer an account to itself — hide whichever is
              // already picked as the other side (#31).
              .filter((p) => String(p.id) !== String(form.destination_payment_mode))
              .map((p) => (
                <option key={p.id} value={p.id}>
                  {p.name}
                </option>
              ))}
          </select>
        </div>
      </div>

      {form.type === 'TRANSFER' && (
        <div>
          <label
            htmlFor="transaction-destination-payment-mode"
            className="block text-xs font-medium text-gray-600 mb-1"
          >
            To Account
          </label>
          <select
            id="transaction-destination-payment-mode"
            value={form.destination_payment_mode}
            onChange={set('destination_payment_mode')}
            required
            className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
          >
            <option value="">Select…</option>
            {payModes
              .filter((p) => String(p.id) !== String(form.payment_mode))
              .map((p) => (
                <option key={p.id} value={p.id}>
                  {p.name}
                </option>
              ))}
          </select>
        </div>
      )}

      {/* ── Recurrent toggle (not applicable to transfers — #31) ── */}
      {form.type !== 'TRANSFER' && (
        <div className="flex items-center gap-3">
          <button
            type="button"
            aria-label="Recurring transaction"
            onClick={() => setVal('is_recurrent', !form.is_recurrent)}
            className={classNames(
              'relative inline-flex h-5 w-9 shrink-0 rounded-full border-2 border-transparent',
              'transition-colors focus:outline-none',
              form.is_recurrent ? 'bg-indigo-600' : 'bg-gray-200',
            )}
          >
            <span
              className={classNames(
                'inline-block h-4 w-4 rounded-full bg-white shadow transition-transform',
                form.is_recurrent ? 'translate-x-4' : 'translate-x-0',
              )}
            />
          </button>
          <span className="text-sm text-gray-600">Recurring transaction</span>
        </div>
      )}

      {/* ── Recurrent period (only if recurrent) ──────────────── */}
      {form.type !== 'TRANSFER' && form.is_recurrent && (
        <div className="grid grid-cols-2 gap-3 pl-2 border-l-2 border-indigo-200">
          <div>
            <label
              htmlFor="transaction-recurrent-period"
              className="block text-xs font-medium text-gray-600 mb-1"
            >
              Every
            </label>
            <input
              id="transaction-recurrent-period"
              type="number"
              min="1"
              value={form.recurrent_period}
              onChange={set('recurrent_period')}
              className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
            />
          </div>
          <div>
            <label
              htmlFor="transaction-recurrent-unit"
              className="block text-xs font-medium text-gray-600 mb-1"
            >
              Unit
            </label>
            <select
              id="transaction-recurrent-unit"
              value={form.recurrent_unit}
              onChange={set('recurrent_unit')}
              className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
            >
              {['DAYS', 'WEEKS', 'MONTHS'].map((u) => (
                <option key={u} value={u}>
                  {capitalize(u)}
                </option>
              ))}
            </select>
          </div>
        </div>
      )}

      {/* ── Submit ─────────────────────────────────────────────── */}
      <button
        type="submit"
        disabled={loading}
        className="w-full py-2.5 bg-indigo-600 text-white text-sm font-medium rounded-lg hover:bg-indigo-700 disabled:opacity-60 disabled:cursor-not-allowed"
      >
        {loading
          ? 'Saving…'
          : `${initial?.id ? 'Update' : 'Create'} ${capitalize(form.type)}`}
      </button>
    </form>
  );
}
